// src/pages/finance-portal/reports/CustomReportBuilder.js

import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Alert, Grid, Autocomplete,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, CircularProgress, Stack,
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import axiosInstance from '../../../utils/axiosInstance';
import {
    fontFamily, ACCENT, formatCurrency, firstOfYearIso, todayIso, downloadCsv,
} from './reportShared';

export default function CustomReportBuilder() {
    const [accounts, setAccounts] = useState([]);
    const [rows, setRows] = useState([{ account_id: '' }]);
    const [columns, setColumns] = useState([{
        id: 'col1', label: 'YTD', filter: { start_date: firstOfYearIso(), end_date: todayIso() },
    }]);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const res = await axiosInstance.get('/api/finance/coa', { params: { flat: true } });
                if (res.data.success) setAccounts(res.data.data || []);
            } catch (e) { /* silent */ }
        })();
    }, []);

    const addRow = () => setRows([...rows, { account_id: '' }]);
    const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
    const updateRow = (i, acct) => {
        setRows(rows.map((r, idx) => idx === i ? { ...r, account_id: acct?.id } : r));
    };

    const addColumn = () => setColumns([...columns, {
        id: `col${columns.length + 1}`,
        label: `Column ${columns.length + 1}`,
        filter: { start_date: firstOfYearIso(), end_date: todayIso() },
    }]);
    const removeColumn = (i) => setColumns(columns.filter((_, idx) => idx !== i));
    const updateColumn = (i, patch) => {
        setColumns(columns.map((c, idx) => idx === i ? { ...c, ...patch } : c));
    };

    const run = async () => {
        setLoading(true); setError(null);
        try {
            const validRows = rows.filter((r) => r.account_id);
            if (!validRows.length) {
                setError('Add at least one account row');
                setLoading(false);
                return;
            }
            const res = await axiosInstance.post('/api/finance/reports/custom/run', {
                rows: validRows,
                columns,
            });
            if (res.data.success) setResult(res.data);
            else setError(res.data.message || 'Failed');
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to run report');
        } finally { setLoading(false); }
    };

    const exportCsv = () => {
        if (!result) return;
        downloadCsv('custom_report.csv', result.rows);
    };

    return (
        <Box sx={{ p: 3, fontFamily }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <BuildIcon sx={{ color: ACCENT }} />
                <Typography variant="h5" sx={{ fontFamily, fontWeight: 700 }}>Custom Report Builder</Typography>
            </Stack>

            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Rows (Accounts)</Typography>
                            <Button size="small" startIcon={<AddIcon />} onClick={addRow}>Add Row</Button>
                        </Stack>
                        {rows.map((r, i) => (
                            <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                <Autocomplete size="small" options={accounts} sx={{ flex: 1 }}
                                    getOptionLabel={(a) => a ? `${a.account_number} — ${a.name}` : ''}
                                    value={accounts.find((a) => a.id === r.account_id) || null}
                                    onChange={(_, v) => updateRow(i, v)}
                                    renderInput={(p) => <TextField {...p} placeholder="Pick account" />} />
                                <IconButton size="small" onClick={() => removeRow(i)}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        ))}
                    </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Columns</Typography>
                            <Button size="small" startIcon={<AddIcon />} onClick={addColumn}>Add Column</Button>
                        </Stack>
                        {columns.map((c, i) => (
                            <Box key={i} sx={{ mb: 2, p: 1, border: '1px solid #e5e7eb', borderRadius: 1 }}>
                                <Grid container spacing={1} alignItems="center">
                                    <Grid item xs={12} sm={3}>
                                        <TextField label="Label" size="small" fullWidth value={c.label}
                                            onChange={(e) => updateColumn(i, { label: e.target.value })} />
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <TextField label="Start" type="date" size="small" fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            value={c.filter?.start_date || ''}
                                            onChange={(e) => updateColumn(i, {
                                                filter: { ...c.filter, start_date: e.target.value },
                                            })} />
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <TextField label="End" type="date" size="small" fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            value={c.filter?.end_date || ''}
                                            onChange={(e) => updateColumn(i, {
                                                filter: { ...c.filter, end_date: e.target.value },
                                            })} />
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                        <TextField label="Formula (optional)" size="small" fullWidth
                                            value={c.formula || ''}
                                            onChange={(e) => updateColumn(i, { formula: e.target.value })} />
                                    </Grid>
                                    <Grid item xs={12} sm={1}>
                                        <IconButton size="small" onClick={() => removeColumn(i)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            </Box>
                        ))}
                    </Paper>
                </Grid>
            </Grid>

            <Stack direction="row" spacing={1} sx={{ my: 2 }}>
                <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={run}
                    sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#15803d' } }}>Run Report</Button>
                {result && (
                    <Button startIcon={<DownloadIcon />} onClick={exportCsv}>Export CSV</Button>
                )}
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

            {result && !loading && (
                <Paper sx={{ p: 2, overflowX: 'auto' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Account</TableCell>
                                    {result.columns.map((c) => (
                                        <TableCell key={c.id} align="right">{c.label}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {result.rows.map((r) => (
                                    <TableRow key={r.account_id}>
                                        <TableCell>{r.account_number} — {r.account_name}</TableCell>
                                        {result.columns.map((c) => (
                                            <TableCell key={c.id} align="right">
                                                {formatCurrency(r[c.id])}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                                <TableRow sx={{ '& td': { borderTop: '2px solid #111827', fontWeight: 700 } }}>
                                    <TableCell>Total</TableCell>
                                    {result.columns.map((c) => (
                                        <TableCell key={c.id} align="right">
                                            {formatCurrency(result.totals[c.id])}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Box>
    );
}
